/**
 * Debug logging system with production safety
 * Reduces console spam while preserving important debugging capability
 */

// Debug levels
export enum LogLevel {
  ERROR = 0,
  WARN = 1, 
  INFO = 2,
  DEBUG = 3
}

// Determine if debug mode is active
const isDebugMode = (): boolean => {
  // Enable debug in development OR for admin users OR with debug flag
  return (
    import.meta.env.DEV || 
    window.location.search.includes('debug=true') ||
    localStorage.getItem('debug_mode') === 'true'
  );
};

// Admin FID for admin-only logs
const ADMIN_FID = 457246;

class DebugLogger {
  private isDebug: boolean;
  private currentFid: number | null = null;

  constructor() {
    this.isDebug = isDebugMode();
  }

  setCurrentFid(fid: number | null) {
    this.currentFid = fid;
  }

  // Standard logging methods
  error(message: string, ...args: any[]) {
    console.error(`❌ ${message}`, ...args);
  }

  warn(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.warn(`⚠️ ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`ℹ️ ${message}`, ...args);
    }
  }

  debug(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`🔧 ${message}`, ...args);
    }
  }

  // Specialized logging methods
  sync(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`🔄 SYNC: ${message}`, ...args);
    }
  }

  device(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`📱 DEVICE: ${message}`, ...args);
    }
  }

  streak(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`🔥 STREAK: ${message}`, ...args);
    }
  }

  save(message: string, ...args: any[]) {
    if (this.isDebug) {
      console.log(`💾 SAVE: ${message}`, ...args);
    }
  }

  // Admin-only logging
  admin(message: string, ...args: any[]) {
    if (this.isDebug && this.currentFid === ADMIN_FID) {
      console.log(`🔧 ADMIN: ${message}`, ...args);
    }
  }

  // Minimal logging - always shows key info even in production
  minimal(message: string, ...args: any[]) {
    console.log(`✅ ${message}`, ...args);
  }

  // Critical operations - always logged
  critical(message: string, ...args: any[]) {
    console.log(`🚨 CRITICAL: ${message}`, ...args);
  }
}

// Export singleton instance
export const debugLogger = new DebugLogger();

// Convenience exports for common patterns
export const log = debugLogger;